// <div data-controller="time-on-page"
//      data-time-on-page-category-id-value="12"
//      data-time-on-page-layout-value="layout_a"
//      data-time-on-page-preview-value="true">   <!-- only on ?layout= previews -->
//   ... category page content ...
// </div>
import { Controller } from "@hotwired/stimulus"
import ahoy from "ahoy.js"

const MAX_SECONDS = 1800 // 30-minute cap on runaway sessions (tab left open)
const MIN_MS = 1000 // skip sub-second bounces

// Tracks one "Time on Page" Ahoy event per page view, counting only time the
// tab was actually visible. category_id is Number-typed: the reports filter
// with JSON_CONTAINS, which won't match a string "12" against an integer.
export default class extends Controller {
  static values = { categoryId: Number, layout: String, preview: Boolean }

  connect() {
    // Turbo preview renders a cached copy before the real page; a ?layout=
    // preview is an admin eyeballing a variant. Neither is a visitor.
    this.preview = document.documentElement.hasAttribute("data-turbo-preview") || this.previewValue
    if (this.preview) return

    this.startSession()

    this.onVisibilityChange = this.visibilityChanged.bind(this)
    this.onPagehide = this.send.bind(this)
    this.onPageshow = this.pageRestored.bind(this)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    window.addEventListener("pagehide", this.onPagehide)
    window.addEventListener("pageshow", this.onPageshow)
  }

  // Covers Turbo navigations, where pagehide never fires. Tab closes go
  // through the pagehide listener instead, on which ahoy's sendBeacon path
  // still delivers.
  disconnect() {
    if (this.preview) return

    this.send()
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    window.removeEventListener("pagehide", this.onPagehide)
    window.removeEventListener("pageshow", this.onPageshow)
  }

  startSession() {
    this.accumulatedMs = 0
    this.sent = false
    this.visibleSince = document.visibilityState === "visible" ? performance.now() : null
  }

  visibilityChanged() {
    if (document.visibilityState === "hidden") {
      this.pause()
    } else if (this.visibleSince === null) {
      this.visibleSince = performance.now()
    }
  }

  // A bfcache restore resurrects the page (and this controller) without a
  // fresh connect(), so re-arm for a new view.
  pageRestored(event) {
    if (event.persisted) this.startSession()
  }

  pause() {
    if (this.visibleSince !== null) {
      this.accumulatedMs += performance.now() - this.visibleSince
      this.visibleSince = null
    }
  }

  send() {
    if (this.sent) return

    this.pause()
    if (this.accumulatedMs < MIN_MS) return

    this.sent = true
    ahoy.track("Time on Page", {
      category_id: this.categoryIdValue,
      seconds: Math.min(Math.round(this.accumulatedMs / 1000), MAX_SECONDS),
      layout: this.layoutValue
    })
  }
}
